import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { formatDate, formatPhone, STATUS_COLORS, STATUS_LABELS, TEMP_COLORS, SOURCE_LABELS, apiErrorMsg } from '@/lib/formatters';
import {
  Plus, Search, Filter, Download, Upload, Phone, Mail, ChevronLeft, ChevronRight,
  Eye, Edit, UserPlus, MoreVertical, ExternalLink, X, ArrowRight
} from 'lucide-react';

const LEAD_STATUSES = Object.keys(STATUS_LABELS);

export default function LeadsPage() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [tempFilter, setTempFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [showAssign, setShowAssign] = useState(null);
  const [executives, setExecutives] = useState([]);
  const [projects, setProjects] = useState([]);
  const [view, setView] = useState('table');
  const [pipeline, setPipeline] = useState({});

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 25, search };
      if (statusFilter) params.status = statusFilter;
      if (sourceFilter) params.source = sourceFilter;
      if (tempFilter) params.temperature = tempFilter;
      const { data } = await api.get('/leads', { params });
      setLeads(data.leads);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, sourceFilter, tempFilter]);

  const fetchPipeline = useCallback(async () => {
    try {
      const { data } = await api.get('/leads/pipeline');
      setPipeline(data);
    } catch {}
  }, []);

  useEffect(() => {
    if (view === 'table') fetchLeads();
    else fetchPipeline();
  }, [fetchLeads, fetchPipeline, view]);

  useEffect(() => {
    api.get('/users/executives').then(r => setExecutives(r.data.executives)).catch(() => {});
    api.get('/projects').then(r => setProjects(r.data.projects)).catch(() => {});
  }, []);

  const clearFilters = () => {
    setSearch(''); setStatusFilter(''); setSourceFilter(''); setTempFilter(''); setPage(1);
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/leads/export', {
        params: { search, status: statusFilter, source: sourceFilter, temperature: tempFilter },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'leads_export.csv'; a.click();
      toast.success('Leads exported');
    } catch (err) {
      toast.error(apiErrorMsg(err));
    }
  };

  return (
    <div className="space-y-4" data-testid="leads-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Leads</h1>
          <p className="text-slate-500 text-sm mt-0.5">{total} total leads</p>
        </div>
        <div className="flex items-center gap-2">
          {user?.role !== 'finance' && (
            <Button onClick={() => setShowCreate(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-lead-btn">
              <Plus size={16} /> Add Lead
            </Button>
          )}
          {['director', 'manager'].includes(user?.role) && (
            <Button variant="outline" onClick={handleExport} data-testid="export-leads-btn">
              <Download size={16} /> Export
            </Button>
          )}
        </div>
      </div>

      <Tabs value={view} onValueChange={setView}>
        <TabsList>
          <TabsTrigger value="table" data-testid="leads-table-tab">Table View</TabsTrigger>
          <TabsTrigger value="pipeline" data-testid="leads-pipeline-tab">Pipeline</TabsTrigger>
        </TabsList>

        <TabsContent value="table" className="space-y-4">
          {/* Filters */}
          <Card className="border-slate-200">
            <CardContent className="p-3">
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative flex-1 min-w-[200px]">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search name, phone, email, ID..."
                    value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                    className="pl-9 h-9 text-sm" data-testid="lead-search-input"
                  />
                </div>
                <Select value={statusFilter} onValueChange={v => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
                  <SelectTrigger className="w-[150px] h-9 text-sm" data-testid="status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {LEAD_STATUSES.map(s => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={sourceFilter} onValueChange={v => { setSourceFilter(v === 'all' ? '' : v); setPage(1); }}>
                  <SelectTrigger className="w-[150px] h-9 text-sm" data-testid="source-filter">
                    <SelectValue placeholder="Source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sources</SelectItem>
                    {Object.entries(SOURCE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={tempFilter} onValueChange={v => { setTempFilter(v === 'all' ? '' : v); setPage(1); }}>
                  <SelectTrigger className="w-[120px] h-9 text-sm" data-testid="temp-filter">
                    <SelectValue placeholder="Temp" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="hot">Hot</SelectItem>
                    <SelectItem value="warm">Warm</SelectItem>
                    <SelectItem value="cold">Cold</SelectItem>
                  </SelectContent>
                </Select>
                {(search || statusFilter || sourceFilter || tempFilter) && (
                  <Button variant="ghost" size="sm" onClick={clearFilters}><X size={14} /> Clear</Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          <div className="table-container bg-white">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold text-xs uppercase tracking-wider">Lead ID</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wider">Name</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wider">Phone</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wider">Project</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wider">Status</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wider">Temp</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wider">Source</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wider">Created</TableHead>
                  <TableHead className="font-semibold text-xs uppercase tracking-wider">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 9 }).map((_, j) => (
                        <TableCell key={j}><div className="h-4 bg-slate-100 rounded animate-pulse" /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : leads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12 text-slate-400">
                      No leads found. {user?.role !== 'finance' && 'Click "Add Lead" to create one.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  leads.map(lead => (
                    <TableRow key={lead.lead_id} data-testid={`lead-row-${lead.lead_id}`} className="cursor-pointer hover:bg-slate-50/80">
                      <TableCell className="font-mono text-xs text-slate-500">{lead.lead_id}</TableCell>
                      <TableCell>
                        <button onClick={() => setShowDetail(lead.lead_id)} className="font-semibold text-slate-800 hover:text-emerald-600 text-sm text-left">
                          {lead.name}
                        </button>
                      </TableCell>
                      <TableCell>
                        <a href={`https://wa.me/${lead.phone?.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-sm text-slate-600 hover:text-emerald-600 flex items-center gap-1">
                          {formatPhone(lead.phone)} <ExternalLink size={12} />
                        </a>
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">{lead.preferred_project_name || '-'}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[lead.status] || 'bg-slate-100 text-slate-700'}`}>
                          {STATUS_LABELS[lead.status] || lead.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${TEMP_COLORS[lead.temperature] || ''}`}>
                          {lead.temperature?.toUpperCase()}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">{SOURCE_LABELS[lead.source] || lead.source}</TableCell>
                      <TableCell className="text-xs text-slate-500">{formatDate(lead.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowDetail(lead.lead_id)} data-testid={`view-lead-${lead.lead_id}`}>
                            <Eye size={14} />
                          </Button>
                          {['director', 'manager'].includes(user?.role) && (
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowAssign(lead)} data-testid={`assign-lead-${lead.lead_id}`}>
                              <UserPlus size={14} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            {/* Pagination */}
            {pages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                <p className="text-sm text-slate-500">Page {page} of {pages} ({total} leads)</p>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                    <ChevronLeft size={14} /> Prev
                  </Button>
                  <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>
                    Next <ChevronRight size={14} />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Pipeline / Kanban View */}
        <TabsContent value="pipeline">
          <div className="kanban-board" data-testid="pipeline-board">
            {LEAD_STATUSES.map(status => (
              <div key={status} className="kanban-column pipeline-col">
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${STATUS_COLORS[status]}`}>
                    {STATUS_LABELS[status]}
                  </span>
                  <span className="text-xs text-slate-400 font-mono">{pipeline[status]?.length || 0}</span>
                </div>
                <div className="space-y-2">
                  {(pipeline[status] || []).map(lead => (
                    <div key={lead.lead_id} className="kanban-card" onClick={() => setShowDetail(lead.lead_id)} data-testid={`pipeline-card-${lead.lead_id}`}>
                      <p className="text-sm font-semibold text-slate-800 mb-1">{lead.name}</p>
                      <p className="text-xs text-slate-500 mb-1">{lead.phone}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-slate-400">{lead.preferred_project_name}</span>
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold ${TEMP_COLORS[lead.temperature] || ''}`}>
                          {lead.temperature}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Lead Dialog */}
      <CreateLeadDialog open={showCreate} onClose={() => setShowCreate(false)} projects={projects} executives={executives} onCreated={() => { fetchLeads(); setShowCreate(false); }} />

      {/* Lead Detail Dialog */}
      {showDetail && <LeadDetailDialog leadId={showDetail} onClose={() => setShowDetail(null)} executives={executives} onUpdated={fetchLeads} user={user} />}

      {/* Assign Dialog */}
      {showAssign && <AssignDialog lead={showAssign} executives={executives} onClose={() => setShowAssign(null)} onAssigned={fetchLeads} />}
    </div>
  );
}

function CreateLeadDialog({ open, onClose, projects, executives, onCreated }) {
  const [form, setForm] = useState({ name: '', phone: '', email: '', city: 'Vadodara', source: 'walk_in', temperature: 'warm', preferred_project: '', preferred_project_name: '', configuration: '', requirements: '', assigned_to: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const proj = projects.find(p => p._id === form.preferred_project);
      await api.post('/leads', { ...form, preferred_project_name: proj?.name || '' });
      toast.success('Lead created successfully');
      setForm({ name: '', phone: '', email: '', city: 'Vadodara', source: 'walk_in', temperature: 'warm', preferred_project: '', preferred_project_name: '', configuration: '', requirements: '', assigned_to: '' });
      onCreated();
    } catch (err) {
      toast.error(apiErrorMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Lead</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Full Name *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required data-testid="create-lead-name" />
            </div>
            <div>
              <Label>Phone *</Label>
              <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+91 XXXXX XXXXX" required data-testid="create-lead-phone" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="create-lead-email" />
            </div>
            <div>
              <Label>City</Label>
              <Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} />
            </div>
            <div>
              <Label>Source</Label>
              <Select value={form.source} onValueChange={v => setForm({ ...form, source: v })}>
                <SelectTrigger data-testid="create-lead-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(SOURCE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Project</Label>
              <Select value={form.preferred_project} onValueChange={v => setForm({ ...form, preferred_project: v })}>
                <SelectTrigger data-testid="create-lead-project"><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>
                  {projects.map(p => <SelectItem key={p._id} value={p._id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Temperature</Label>
              <Select value={form.temperature} onValueChange={v => setForm({ ...form, temperature: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hot">Hot</SelectItem>
                  <SelectItem value="warm">Warm</SelectItem>
                  <SelectItem value="cold">Cold</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Requirements / Notes</Label>
              <Textarea value={form.requirements} onChange={e => setForm({ ...form, requirements: e.target.value })} rows={2} data-testid="create-lead-requirements" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-lead-submit">
              {saving ? 'Creating...' : 'Create Lead'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function LeadDetailDialog({ leadId, onClose, executives, onUpdated, user }) {
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actForm, setActForm] = useState({ type: 'call', description: '', outcome: '', notes: '', next_followup: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await api.get(`/leads/${leadId}`);
        setLead(data.lead);
        setActivities(data.activities);
      } catch {} finally { setLoading(false); }
    };
    fetch();
  }, [leadId]);

  const logActivity = async () => {
    if (!actForm.description) return toast.error('Description required');
    setSaving(true);
    try {
      await api.post('/activities', { lead_id: leadId, ...actForm });
      toast.success('Activity logged');
      setActForm({ type: 'call', description: '', outcome: '', notes: '', next_followup: '' });
      const { data } = await api.get(`/leads/${leadId}`);
      setLead(data.lead);
      setActivities(data.activities);
      onUpdated();
    } catch (err) { toast.error(apiErrorMsg(err)); }
    finally { setSaving(false); }
  };

  if (loading) return <Dialog open onOpenChange={onClose}><DialogContent><p>Loading...</p></DialogContent></Dialog>;
  if (!lead) return null;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {lead.name}
            <span className="font-mono text-xs text-slate-400">{lead.lead_id}</span>
            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[lead.status]}`}>{STATUS_LABELS[lead.status]}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm mb-4">
          <div><span className="text-slate-400">Phone:</span> <span className="font-medium">{lead.phone}</span></div>
          <div><span className="text-slate-400">Email:</span> <span className="font-medium">{lead.email || '-'}</span></div>
          <div><span className="text-slate-400">City:</span> <span className="font-medium">{lead.city}</span></div>
          <div><span className="text-slate-400">Source:</span> <span className="font-medium">{SOURCE_LABELS[lead.source] || lead.source}</span></div>
          <div><span className="text-slate-400">Project:</span> <span className="font-medium">{lead.preferred_project_name || '-'}</span></div>
          <div><span className="text-slate-400">Config:</span> <span className="font-medium">{lead.configuration || '-'}</span></div>
          <div><span className="text-slate-400">Temperature:</span> <span className={`font-bold ${lead.temperature === 'hot' ? 'text-red-500' : lead.temperature === 'warm' ? 'text-amber-500' : 'text-blue-500'}`}>{lead.temperature?.toUpperCase()}</span></div>
          <div><span className="text-slate-400">Next Follow-up:</span> <span className="font-medium">{lead.next_followup ? formatDate(lead.next_followup) : '-'}</span></div>
          {lead.requirements && <div className="col-span-2"><span className="text-slate-400">Requirements:</span> <span className="font-medium">{lead.requirements}</span></div>}
        </div>

        {/* Log Activity */}
        {user?.role !== 'finance' && (
          <Card className="border-slate-200 mb-4">
            <CardHeader className="py-3">
              <CardTitle className="text-sm font-semibold">Log Activity</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Select value={actForm.type} onValueChange={v => setActForm({ ...actForm, type: v })}>
                  <SelectTrigger data-testid="activity-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="call">Call</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="meeting">Meeting</SelectItem>
                    <SelectItem value="site_visit">Site Visit</SelectItem>
                    <SelectItem value="note">Note</SelectItem>
                  </SelectContent>
                </Select>
                <Input placeholder="Outcome" value={actForm.outcome} onChange={e => setActForm({ ...actForm, outcome: e.target.value })} />
              </div>
              <Input placeholder="Description *" value={actForm.description} onChange={e => setActForm({ ...actForm, description: e.target.value })} data-testid="activity-description" />
              <div className="grid grid-cols-2 gap-3">
                <Input type="datetime-local" value={actForm.next_followup} onChange={e => setActForm({ ...actForm, next_followup: e.target.value })} />
                <Button onClick={logActivity} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="log-activity-btn">
                  {saving ? 'Saving...' : 'Log Activity'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Activity Timeline */}
        <div>
          <h4 className="text-sm font-semibold text-slate-700 mb-3">Activity Timeline</h4>
          {activities.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">No activities yet</p>
          ) : (
            <div className="space-y-3 max-h-60 overflow-y-auto">
              {activities.map(act => (
                <div key={act._id} className="flex gap-3 text-sm">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 mt-2 flex-shrink-0" />
                  <div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px]">{act.type}</Badge>
                      <span className="text-xs text-slate-400">{formatDate(act.created_at)}</span>
                    </div>
                    <p className="text-slate-700 mt-0.5">{act.description}</p>
                    {act.outcome && <p className="text-slate-500 text-xs mt-0.5">Outcome: {act.outcome}</p>}
                    {act.created_by_name && <p className="text-slate-400 text-xs">By: {act.created_by_name}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AssignDialog({ lead, executives, onClose, onAssigned }) {
  const [assignTo, setAssignTo] = useState(lead.assigned_to || '');
  const [saving, setSaving] = useState(false);

  const handleAssign = async () => {
    setSaving(true);
    try {
      await api.put(`/leads/${lead.lead_id}/assign`, { assigned_to: assignTo });
      toast.success('Lead assigned');
      onAssigned();
      onClose();
    } catch (err) { toast.error(apiErrorMsg(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Assign Lead: {lead.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Label>Select Executive</Label>
          <Select value={assignTo} onValueChange={setAssignTo}>
            <SelectTrigger data-testid="assign-executive-select"><SelectValue placeholder="Choose..." /></SelectTrigger>
            <SelectContent>
              {executives.map(e => <SelectItem key={e._id} value={e._id}>{e.name} ({e.role})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleAssign} disabled={saving || !assignTo} className="bg-emerald-600 hover:bg-emerald-700" data-testid="assign-submit-btn">
            {saving ? 'Assigning...' : 'Assign'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
